import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import {
  MessageTemplateCategoryCreateDocument,
  MessageTemplateCategoryCreateMutation,
  MessageTemplateCategoryCreateMutationVariables,
  MessageTemplateCategoryFragment,
  MessageTemplateCategoryUpdateDocument,
  MessageTemplateCategoryUpdateMutation,
  MessageTemplateCategoryUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  title: string;
};

type Props = {
  autofocus: boolean;
  messageTemplateCategory?: MessageTemplateCategoryFragment | null;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const MessageTemplateCategoryForm = ({ autofocus, messageTemplateCategory, onCancel, onError, onSuccess }: Props) => {
  const initialValues: FormData = {
    title: messageTemplateCategory?.title ?? ''
  };

  const intl = useIntl();

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createMessageTemplateCategory, { client }] = useMutation<
    MessageTemplateCategoryCreateMutation,
    MessageTemplateCategoryCreateMutationVariables
  >(MessageTemplateCategoryCreateDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const [updateMessageTemplateCategory] = useMutation<
    MessageTemplateCategoryUpdateMutation,
    MessageTemplateCategoryUpdateMutationVariables
  >(MessageTemplateCategoryUpdateDocument);

  const schema = yup
    .object()
    .shape({
      title: yup.string().required(intl.formatMessage({ id: 'errors.messageTemplate.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (messageTemplateCategory?.id) {
      return updateMessageTemplateCategory({
        variables: {
          ...form.getValues(),
          id: messageTemplateCategory.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.messageTemplateCategoryUpdate,
          intl,
          onError,
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.messageTemplateCategory'
        });
      });
    }
    return createMessageTemplateCategory({
      variables: {
        ...form.getValues()
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.messageTemplateCategoryCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.messageTemplateCategory'
      });
    });
  }, [
    messageTemplateCategory?.id,
    createMessageTemplateCategory,
    form,
    updateMessageTemplateCategory,
    intl,
    onError,
    onSuccess
  ]);

  return (
    <>
      <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
        <BaseErrors errors={baseErrors} />
        <Input<FormData>
          autofocus={autofocus}
          control={form.control}
          error={form.formState.errors.title}
          label={intl.formatMessage({ id: 'forms.messageTemplateCategory.titleLabel' })}
          name="title"
          required
          spellcheck
        />

        <FormButtons
          hookForm={form}
          onCancel={onCancel}
          saveKey={
            messageTemplateCategory?.id ? 'dictionary.saveChanges' : 'pages.expert.messageTemplateCategories.create'
          }
        />
      </Form>
    </>
  );
};

export default MessageTemplateCategoryForm;
